import { useCallback, useRef, useState } from "react";
import Webcam from "react-webcam";
import { styled } from "@mui/material/styles";
import {
    Dialog,
    DialogContent,
    DialogProps,
    Grid,
    InternalStandardProps,
} from "@mui/material";
import { AppButton } from "../AppButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const StyledDialog = styled(Dialog)({});

const ContentGrid = styled(Grid)({
    display: "flex",
    minHeight: "250px",
    justifyContent: "flex-start",
});

const CamWrapper = styled(Grid)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
}));

const CamStylingWrapper = styled(CamWrapper)({
    overflow: "hidden",
});

const CamRotateIcon = styled(FontAwesomeIcon)({
    height: "40px",
    cursor: "pointer",
});

const CapturedImage = styled("img")({
    objectFit: "contain",
});

export interface WebCamImageDailogProps
    extends InternalStandardProps<DialogProps> {
    onClose: () => void;
    setCapturedImage: (capturedImage: any) => void;
    capturedImage: any;
    onUploadCaptureImage: () => void;
}

export const WebCamImageDailog = (props: WebCamImageDailogProps) => {
    const {
        className,
        capturedImage,
        setCapturedImage,
        onUploadCaptureImage,
        onClose,
        ...rest
    } = props;

    const [videoConstraints, setVideoConstraints] = useState<{
        facingMode: string | { exact: string };
    }>({
        facingMode: "user",
    });

    const webcamRef = useRef(null);
    const capturePhoto = useCallback(() => {
        const imageSrc = webcamRef.current.getScreenshot();
        setCapturedImage(imageSrc);
    }, [webcamRef]);

    const handleToggleCam = () => {
        if (videoConstraints?.facingMode === "user") {
            setVideoConstraints({
                ...videoConstraints,
                facingMode: { exact: "environment" },
            });
        } else {
            setVideoConstraints({
                ...videoConstraints,
                facingMode: "user",
            });
        }
    };

    return (
        <StyledDialog
            maxWidth={"sm"}
            fullWidth
            className={className}
            {...rest}>
            <DialogContent>
                <ContentGrid container>
                    {!capturedImage ? (
                        <>
                            <CamStylingWrapper item xs={12}>
                                <Webcam
                                    ref={webcamRef}
                                    audio={false}
                                    screenshotFormat="image/jpeg"
                                    width={"100%"}
                                    videoConstraints={videoConstraints}
                                />
                            </CamStylingWrapper>
                            <CamWrapper item xs={12}>
                                <AppButton
                                    buttonVariant="grey-text"
                                    onClick={handleToggleCam}>
                                    <CamRotateIcon
                                        icon={["fas", "camera-rotate"]}
                                    />
                                </AppButton>
                                <AppButton
                                    buttonVariant="grey-outlined"
                                    rounded
                                    onClick={capturePhoto}>
                                    Capture
                                </AppButton>
                                &nbsp;&nbsp;
                                <AppButton
                                    buttonVariant="grey-outlined"
                                    rounded
                                    onClick={onClose}>
                                    Cancel
                                </AppButton>
                            </CamWrapper>
                        </>
                    ) : (
                        <>
                            <CamStylingWrapper item xs={12}>
                                <CapturedImage
                                    src={capturedImage}
                                    alt="Not-Found"
                                />
                            </CamStylingWrapper>
                            <CamWrapper item xs={12}>
                                <AppButton
                                    buttonVariant="grey-outlined"
                                    rounded
                                    onClick={() => setCapturedImage(null)}>
                                    Retake
                                </AppButton>
                                &nbsp;&nbsp;
                                <AppButton
                                    buttonVariant="grey-outlined"
                                    rounded
                                    onClick={onUploadCaptureImage}>
                                    Ok
                                </AppButton>
                            </CamWrapper>
                        </>
                    )}
                </ContentGrid>
            </DialogContent>
        </StyledDialog>
    );
};
